import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { StorefrontSummary } from '@aeris/contracts'
import {
  Button,
  ConfirmDialog,
  Field,
  InlineAlert,
  Input,
  Page,
  PageHeader,
  QueryState,
  Select,
  SwitchField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from '@aeris/ui'

import { StorefrontBanners } from '../components/storefront-banners.tsx'
import { StorefrontMenu } from '../components/storefront-menu.tsx'
import { StorefrontMetrics } from '../components/storefront-metrics.tsx'
import {
  ApiError,
  createStorefront,
  fetchBranches,
  fetchStorefrontSessions,
  fetchStorefronts,
  revokeStorefrontSessions,
  updateStorefront,
} from '../lib/api.ts'

/**
 * A loja online (F221, ADR-0041).
 *
 * O site de atacado chama `/api/v1/storefront/<endereço>/...` do navegador do
 * lojista: este é o lugar que diz qual é esse endereço, de qual filial sai o
 * estoque e de quais sites o navegador pode chamar.
 *
 * O lojista entra só com o número do cadastro (decisão do dono, 04/10). Por
 * isso a tela mostra quem está com a loja aberta e deixa derrubar: é o freio
 * de quem descobriu que alguém entrou com o número de outro.
 */

const message = (error: unknown, fallback: string): string =>
  error instanceof ApiError ? error.message : fallback

/** Uma origem por linha; linhas vazias não contam. */
const originsOf = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')

const when = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

export const LojaOnlinePage = (): ReactNode => {
  const stores = useQuery({
    queryKey: ['storefronts'],
    queryFn: ({ signal }) => fetchStorefronts(signal),
  })
  const store = stores.data?.storefronts[0] ?? null

  return (
    <Page>
      <PageHeader
        title="Loja online"
        description="O site de atacado lê o catálogo, o preço e o estoque daqui. O lojista entra com o número do cadastro."
      />
      <QueryState
        isPending={stores.isPending}
        isError={stores.isError}
        error={stores.error}
        onRetry={() => void stores.refetch()}
        what="a loja online"
        isEmpty={false}
        empty={null}
      >
        <div className="flex flex-col gap-6">
          <StoreForm key={store?.version ?? 'nova'} store={store} />
          {/* F269: o carrossel do alto do site. */}
          {store !== null && <StorefrontBanners store={store} />}
          {/* F270: o menu "Categorias" do site. */}
          {store !== null && <StorefrontMenu store={store} />}
          {store !== null && <OpenSessions store={store} />}
          {/* F226: o que acontece na loja, no período. */}
          {store !== null && <StorefrontMetrics store={store} />}
        </div>
      </QueryState>
    </Page>
  )
}

const StoreForm = ({
  store,
}: {
  store: StorefrontSummary | null
}): ReactNode => {
  const queryClient = useQueryClient()
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
  })

  const [name, setName] = useState(store?.name ?? '')
  const [slug, setSlug] = useState(store?.slug ?? '')
  const [branchId, setBranchId] = useState(store?.branchId ?? '')
  const [origins, setOrigins] = useState(
    (store?.allowedOrigins ?? []).join('\n')
  )
  const [active, setActive] = useState(store?.status !== 'archived')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: async () =>
      store === null
        ? await createStorefront({
            name,
            slug,
            branchId,
            allowedOrigins: originsOf(origins),
          })
        : await updateStorefront(store.id, {
            version: store.version,
            name,
            branchId,
            allowedOrigins: originsOf(origins),
            status: active ? 'active' : 'archived',
          }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      await queryClient.invalidateQueries({ queryKey: ['storefronts'] })
    },
    onError: (failure: unknown) => {
      setSaved(false)
      setError(message(failure, 'Não foi possível salvar a loja.'))
    },
  })

  const branchOptions = (branches.data?.branches ?? []).filter(
    (branch) => branch.status === 'active'
  )

  return (
    <form
      className="flex flex-col gap-4 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <h2 className="text-(length:--text-sm) font-(--weight-medium)">
        {store === null ? 'Abrir a loja online' : 'A loja'}
      </h2>

      {error !== null && (
        <InlineAlert tone="danger" title="Não deu certo">
          {error}
        </InlineAlert>
      )}
      {saved && error === null && (
        <InlineAlert tone="success" title="Salvo">
          A loja online foi atualizada.
        </InlineAlert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Nome" required>
          {(field) => (
            <Input
              {...field}
              value={name}
              maxLength={80}
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          )}
        </Field>

        <Field
          label="Endereço"
          required
          hint={
            store === null
              ? 'Letras minúsculas, números e hífen. O site chama /api/v1/storefront/<endereço>.'
              : `O site chama /api/v1/storefront/${store.slug}. O endereço não muda depois de criado.`
          }
        >
          {(field) => (
            <Input
              {...field}
              value={slug}
              disabled={store !== null}
              maxLength={40}
              placeholder="elite-gv"
              onChange={(event) => {
                setSlug(event.target.value.toLowerCase())
              }}
            />
          )}
        </Field>

        <Field
          label="Filial"
          required
          hint="O estoque que a loja mostra é o desta filial."
        >
          {(field) => (
            <Select
              {...field}
              value={branchId}
              disabled={branches.isPending}
              onChange={(event) => {
                setBranchId(event.target.value)
              }}
            >
              <option value="">Escolha a filial</option>
              {branchOptions.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Sites que podem chamar a loja"
          hint="Um por linha, com https:// — por exemplo https://elitedigitalatacado.com.br."
        >
          {(field) => (
            <Textarea
              {...field}
              rows={3}
              value={origins}
              onChange={(event) => {
                setOrigins(event.target.value)
              }}
            />
          )}
        </Field>
      </div>

      {store !== null && (
        <SwitchField
          label="Loja no ar"
          description="Desligada, o site não acha a loja e ninguém entra."
          checked={active}
          onCheckedChange={(checked) => {
            setActive(checked)
          }}
        />
      )}

      <div>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={save.isPending}
          disabled={name.trim() === '' || slug.trim() === '' || branchId === ''}
        >
          {store === null ? 'Abrir a loja' : 'Salvar'}
        </Button>
      </div>
    </form>
  )
}

const OpenSessions = ({ store }: { store: StorefrontSummary }): ReactNode => {
  const queryClient = useQueryClient()
  const [dropping, setDropping] = useState<{
    partyId: string
    name: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sessions = useQuery({
    queryKey: ['storefront-sessions', store.id],
    queryFn: ({ signal }) => fetchStorefrontSessions(store.id, signal),
  })

  const revoke = useMutation({
    mutationFn: async (partyId: string) =>
      await revokeStorefrontSessions(store.id, partyId),
    onSuccess: async () => {
      setDropping(null)
      setError(null)
      await queryClient.invalidateQueries({
        queryKey: ['storefront-sessions', store.id],
      })
    },
    onError: (failure: unknown) => {
      setError(message(failure, 'Não foi possível derrubar as sessões.'))
    },
  })

  const rows = sessions.data?.sessions ?? []

  return (
    <section className="flex flex-col gap-3 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4">
      <h2 className="text-(length:--text-sm) font-(--weight-medium)">
        Quem está com a loja aberta
      </h2>
      <p className="text-(length:--text-xs) text-(--text-muted)">
        O lojista entra só com o número. Se alguém entrou com o número de outro,
        derrube aqui: ele sai na hora e precisa entrar de novo.
      </p>

      {error !== null && (
        <InlineAlert tone="danger" title="Não deu certo">
          {error}
        </InlineAlert>
      )}

      <QueryState
        isPending={sessions.isPending}
        isError={sessions.isError}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        what="as sessões da loja"
        isEmpty={rows.length === 0}
        empty={
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Ninguém está com a loja aberta agora.
          </p>
        }
      >
        <TableContainer>
          <Table aria-label="Quem está com a loja aberta">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Cliente</TableHeaderCell>
                <TableHeaderCell className="w-24">Código</TableHeaderCell>
                <TableHeaderCell className="w-24">Aparelhos</TableHeaderCell>
                <TableHeaderCell className="w-36">
                  Último acesso
                </TableHeaderCell>
                <TableHeaderCell className="w-28">
                  <span className="sr-only">Ação</span>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.partyId}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.code ?? '—'}</TableCell>
                  <TableCell>{row.sessions}</TableCell>
                  <TableCell>{when(row.lastSeenAt)}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDropping({ partyId: row.partyId, name: row.name })
                      }}
                    >
                      Derrubar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <ConfirmDialog
        open={dropping !== null}
        onOpenChange={(next) => {
          if (!next) setDropping(null)
        }}
        title={`Derrubar ${dropping?.name ?? ''}?`}
        description="Todas as sessões deste cliente na loja online acabam agora. Ele precisa entrar de novo com o número."
        confirmLabel="Derrubar"
        cancelLabel="Voltar"
        loading={revoke.isPending}
        onConfirm={() => {
          if (dropping !== null) revoke.mutate(dropping.partyId)
        }}
      />
    </section>
  )
}
